import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';

interface WorkScopeSettingsProps {
  userProfile: any;
  onUpdate?: () => void;
}

const WorkScopeSettings: React.FC<WorkScopeSettingsProps> = ({ userProfile, onUpdate }) => {
  const [workScope, setWorkScope] = useState<string>('lokaal');
  const [workScopeCountry, setWorkScopeCountry] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (userProfile) {
      setWorkScope(userProfile.workScope || userProfile.onboardingData?.workScope || 'lokaal');
      setWorkScopeCountry(userProfile.workScopeCountry || userProfile.onboardingData?.workScopeCountry || '');
    }
  }, [userProfile]);

  const handleSave = async () => {
    if (!auth.currentUser?.uid) return;

    setLoading(true);
    try {
      // Update user profile
      const userProfileRef = doc(db, 'userProfiles', auth.currentUser.uid);
      await updateDoc(userProfileRef, {
        workScope,
        workScopeCountry: workScope === 'nationaal' ? workScopeCountry : '',
        updatedAt: new Date()
      });

      // Also update onboarding data for backward compatibility
      const userDoc = await getDoc(userProfileRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const updatedOnboardingData = {
          ...userData.onboardingData,
          workScope,
          workScopeCountry: workScope === 'nationaal' ? workScopeCountry : ''
        };
        
        await updateDoc(userProfileRef, {
          onboardingData: updatedOnboardingData
        });
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating work scope:', error);
      alert('Er is een fout opgetreden bij het opslaan. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  const getScopeDescription = (scope: string) => {
    switch (scope) {
      case 'lokaal':
        return 'Je ziet alleen gebruikers uit je regio';
      case 'nationaal':
        return 'Je ziet gebruikers uit je hele land';
      case 'internationaal':
        return 'Je ziet gebruikers van over de hele wereld';
      default:
        return '';
    }
  };

  const getScopeIcon = (scope: string) => {
    switch (scope) {
      case 'lokaal':
        return '📍';
      case 'nationaal':
        return '🇳🇱';
      case 'internationaal':
        return '🌍';
      default:
        return '';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Werkgebied Instellingen</h3>
      <p className="text-sm text-gray-600 mb-6">
        Kies op welk niveau je wilt netwerken. Dit bepaalt welke gebruikers je ziet in je netwerk.
      </p>

      <div className="space-y-4">
        {/* Work Scope Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Werkgebied
          </label>
          <div className="space-y-3">
            {[
              { value: 'lokaal', label: 'Lokaal', description: 'Alleen je regio' },
              { value: 'nationaal', label: 'Nationaal', description: 'Hele land' },
              { value: 'internationaal', label: 'Internationaal', description: 'Wereldwijd' }
            ].map((option) => (
              <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="workScope"
                  value={option.value}
                  checked={workScope === option.value}
                  onChange={(e) => setWorkScope(e.target.value)}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{getScopeIcon(option.value)}</span>
                    <span className="font-medium text-gray-900">{option.label}</span>
                  </div>
                  <p className="text-sm text-gray-600">{option.description}</p>
                  <p className="text-xs text-gray-500 mt-1">{getScopeDescription(option.value)}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Country Selection for National Scope */}
        {workScope === 'nationaal' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Land voor nationaal werkgebied
            </label>
            <select
              value={workScopeCountry}
              onChange={(e) => setWorkScopeCountry(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Selecteer een land...</option>
              <option value="Nederland">🇳🇱 Nederland</option>
              <option value="België">🇧🇪 België</option>
              <option value="Duitsland">🇩🇪 Duitsland</option>
              <option value="Frankrijk">🇫🇷 Frankrijk</option>
              <option value="Verenigd Koninkrijk">🇬🇧 Verenigd Koninkrijk</option>
              <option value="Verenigde Staten">🇺🇸 Verenigde Staten</option>
              <option value="Canada">🇨🇦 Canada</option>
              <option value="Australië">🇦🇺 Australië</option>
              <option value="Andere">🌍 Andere</option>
            </select>
          </div>
        )}

        {/* Current Settings Display */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Huidige instellingen:</h4>
          <div className="flex items-center space-x-2">
            <span className="text-lg">{getScopeIcon(workScope)}</span>
            <span className="text-sm text-blue-800">
              {workScope === 'lokaal' ? 'Lokaal' :
               workScope === 'nationaal' ? `Nationaal (${workScopeCountry || 'Geen land geselecteerd'})` :
               'Internationaal'}
            </span>
          </div>
          <p className="text-xs text-blue-600 mt-1">{getScopeDescription(workScope)}</p>
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-between pt-4">
          <button
            onClick={handleSave}
            disabled={loading || (workScope === 'nationaal' && !workScopeCountry)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              loading || (workScope === 'nationaal' && !workScopeCountry)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {loading ? 'Opslaan...' : 'Instellingen opslaan'}
          </button>
          
          {saved && (
            <div className="flex items-center space-x-2 text-green-600">
              <span className="text-lg">✅</span>
              <span className="text-sm font-medium">Opgeslagen!</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkScopeSettings;
